import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { UpdateListActionResult } from '@app/types/generated';
import { useHistory, useUrlBuilder } from '@app/router';
import { EXPERIMENTS } from '@app/config';
import { useExperiment } from '@app/experiments';
import { useToast, useDisclosure } from '@stockx/ui';
import { Events, useGATrackEvent } from '@app/analytics';
import { trackEvent as segmentTrackEvent, EVENTS, SCREENS } from '@app/segment';
import useFavoriteProduct from './useFavoriteProduct';
import useUnfavoriteProduct from './useUnfavoriteProduct';
import { favoritedProductsList } from '../state';
import { Product } from '../types';

export interface IUseFavoriteVariants {
  product: Product;
  onOpen: () => void;
  favoritedProduct: boolean;
  onClose?: () => void;
}

const useFavoriteVariants = ({
  product,
  onOpen,
  favoritedProduct,
  onClose,
}: IUseFavoriteVariants) => {
  const { t } = useTranslation();
  const buildUrl = useUrlBuilder();
  const history = useHistory();
  const gaTrack = useGATrackEvent();
  const {
    isOpen: modalErrorIsOpen,
    onOpen: modalErrorOnOpen,
    onClose: modalErrorOnClose,
  } = useDisclosure();

  const toast = useToast();
  const setProductIsFavorited = useSetRecoilState(favoritedProductsList);
  const [isRetrying, setRetry] = useState(false);

  const {
    unfavoriteProduct,
    loading: unfavoriteLoading,
    data: unfavoriteData,
    error: unfavoriteError,
  } = useUnfavoriteProduct();

  const {
    favoriteProduct,
    loading: favoriteLoading,
    data: favoriteData,
    error: favoriteError,
  } = useFavoriteProduct(product);

  const isFavoriteTestEnabled =
    useExperiment(EXPERIMENTS.ab_pirate_product_cell_favorite_web_v1) ===
    'true';

  useEffect(() => {
    if (
      favoriteData &&
      favoriteData?.updateListItems?.status !==
        UpdateListActionResult.FAILURE &&
      !favoriteLoading
    ) {
      onClose?.();
      toast({
        message: t('favorite_confirmation_message'),
        duration: 6000,
        isCloseable: true,
        position: 'bottom',
        action: {
          text: t('favorite_confirmation_link'),
          handleClick: () => history.push(buildUrl('/favorites/all-favorites')),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteData, favoriteLoading]);

  useEffect(() => {
    if (
      unfavoriteData &&
      unfavoriteData?.updateListItems?.status !==
        UpdateListActionResult.FAILURE &&
      !unfavoriteLoading &&
      product?.id
    ) {
      const variants = unfavoriteData?.updateListItems?.items?.reduce<
        Record<string, boolean>
      >((acc, value) => {
        if (value?.item?.id) {
          acc[value.item.id] = !!value.item.favorite;
        }

        return acc;
      }, {});

      setProductIsFavorited({ [product?.id]: false, ...variants });
      onClose?.();
      toast({
        message: t('unfavorite_confirmation_message', {
          productTitle: product?.title,
        }),
        duration: 6000,
        isCloseable: true,
        position: 'bottom',
        action: {
          text: t('unfavorite_confirmation_link'),
          handleClick: () => handleUndo(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfavoriteData, unfavoriteLoading]);

  useEffect(() => {
    if (
      favoriteError ||
      unfavoriteError ||
      favoriteData?.updateListItems?.status ===
        UpdateListActionResult.FAILURE ||
      unfavoriteData?.updateListItems?.status === UpdateListActionResult.FAILURE
    ) {
      onClose?.();
      modalErrorOnOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfavoriteError, favoriteError, favoriteData, unfavoriteData]);

  useEffect(() => {
    if (isRetrying) {
      if (favoriteError || unfavoriteError) {
        handleFavoriteUpdate();
      } else {
        modalErrorOnClose();
        onOpen();
      }

      setRetry(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRetrying]);

  const handleUndo = async () => {
    await favoriteProductFn();
    trackFavoriteEvent({
      gaEvent: Events.FollowProduct,
      segmentEvent: EVENTS.PRODUCT_FOLLOWED,
      singleSizeVariant: product?.variants?.[0]?.id,
    });
  };

  const favoriteProductFn = async () => {
    await favoriteProduct({
      input: {
        action: 'ADD',
        items: [
          {
            productId: product?.id,
            variantId: product?.variants?.[0]?.id,
          },
        ],
      },
      skipFavorite: !isFavoriteTestEnabled,
    });
  };

  const unfavoriteProductFn = async () => {
    await unfavoriteProduct({
      input: {
        action: 'DELETE',
        items: [
          {
            productId: product?.id,
            variantId: product?.variants?.[0]?.id,
          },
        ],
      },
      skipFavorite: !isFavoriteTestEnabled,
    });
  };

  const trackFavoriteEvent = ({ gaEvent, segmentEvent, singleSizeVariant }) => {
    gaTrack(gaEvent, product?.productCategory, product?.title);

    segmentTrackEvent(segmentEvent, {
      vertical: product?.productCategory,
      screenName: SCREENS.PRODUCT,
      productUUID: product?.id,
      skuUUID: singleSizeVariant || null,
      productName: product?.title,
    });
  };

  const handleFavoriteUpdate = async () => {
    if (favoritedProduct) {
      await unfavoriteProductFn();
      trackFavoriteEvent({
        gaEvent: Events.UnfollowProduct,
        segmentEvent: EVENTS.PRODUCT_UNFOLLOWED,
        singleSizeVariant: product?.variants?.[0]?.id,
      });
    } else {
      await favoriteProductFn();
      trackFavoriteEvent({
        gaEvent: Events.FollowProduct,
        segmentEvent: EVENTS.PRODUCT_FOLLOWED,
        singleSizeVariant: product?.variants?.[0]?.id,
      });
    }
  };

  return {
    handleFavoriteUpdate,
    favoriteLoading,
    unfavoriteLoading,
    setRetry,
    modalErrorIsOpen,
    modalErrorOnClose,
    modalErrorOnOpen,
  };
};

export default useFavoriteVariants;
